import {
    ContextNavigationItem,
    NavigationContext,
    NavigationContextProps,
    NavigationConfig,
    RouteActionItems,
} from '@superbet-group/web.types';
import {
    Location,
    Route,
} from 'vue-router';
import {
    ActionContext,
    Commit,
    Store,
} from 'vuex';

import { logger } from '@app/logger';
import router from '@app/router';
import { Link } from '@app/router/constants/Link';
import { RouteName } from '@app/router/constants/RouteName';
import { getLiveStatusFromQuery } from '@src/app/router/utils';
import { IState } from '@app/store';
import StoreUtil from '@app/store/StoreUtil';
import { TimeFilter } from '@shared/timeFilter/types';
import { removeEventQueryParams } from '@src/app/utils';
import { TimeFilterSlug } from '@store/modules/ui/shared/timeFilter/utils';
import { getTimeFilter } from '@store/modules/ui/sportOffer/sports/rawFunctions';
import { createSimpleGetter } from '@superbet-group/web.lib.vue-utilities';
import Vue from 'vue';
import { stopMeticulousRecording } from '@src/app/utils/meticulous/meticulousUtils';
import { ExpandedEventInUrl } from '@src/app/enums';
import { AvailableCompetitionTabs } from '@sportOfferFederation/competitions/store/ui/types';
import { getSeoEngineUrl } from '@src/app/utils/seoEngine';

function defaultState(): INavigationState {
    return {
        route: null,
        loginGuardedRoute: null,
        topLevelNavigations: {},
        currentNavigationContext: localStorage.getItem('currentNavigationContext') as NavigationContext ?? NavigationContext.Sports,
        isSportBetSlipRehydrated: false,
        navigationConfig: { mobileRouteActionItems: {}, desktopRouteActionItems: {} },
        routeSeoData: {},
    };
}

const onNavigationError = (e: Error) => {
    if (!e || e.name === 'NavigationDuplicated') {
        return;
    }

    logger.error(e as Error);
};

export const push = (_context: unknown, location: Location): Promise<void | Route> => router.push(location).catch(onNavigationError);
export const replace = (_context: unknown, location: Location): Promise<void | Route> => router.replace(location).catch(onNavigationError);
export const back = (): void => router.back();

const SAVE_LOGIN_GUARDED_ROUTE = 'SAVE_LOGIN_GUARDED_ROUTE';
const SPORT_BETSLIP_REHYDRATED = 'SPORT_BETSLIP_REHYDRATED';
const ADD_TOP_LEVEL_NAVIGATION = 'ADD_TOP_LEVEL_NAVIGATION';
const ADD_DESKTOP_NAVIGATION_ITEM = 'ADD_DESKTOP_NAVIGATION_ITEM';
const ADD_MOBILE_NAVIGATION_ITEM = 'ADD_MOBILE_NAVIGATION_ITEM';
const ADD_MOBILE_ROUTE_ACTION_ITEMS = 'ADD_MOBILE_ROUTE_ACTION_ITEMS';
const SET_NAVIGATION_CONTEXT = 'SET_NAVIGATION_CONTEXT';
const SET_ROUTE_SEO_DATA = 'SET_ROUTE_SEO_DATA';

type SeoData = {
    title: string | null,
    seoCopy: string | null,
};

export interface INavigationState {
    route: Route | null;
    loginGuardedRoute: Route | string | null;
    topLevelNavigations: Partial<Record<NavigationContext, NavigationContextProps>>,
    currentNavigationContext: NavigationContext,
    isSportBetSlipRehydrated: boolean;
    navigationConfig: NavigationConfig;
    routeSeoData: Record<string, SeoData>;
}

// This is a workaround to allow federations to add navigation items before the navigation context is set
const desktopItemsAddedByFederations: Partial<Record<NavigationContext, ContextNavigationItem[]>> = {};
const mobileItemsAddedByFederations: Partial<Record<NavigationContext, ContextNavigationItem[]>> = {};

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
export const navigationModule = {
    namespaced: true,

    state: defaultState(),

    actions: {
        push,
        replace,
        back,
        saveLoginGuardedRoute: StoreUtil.createSimpleMutatorAction(SAVE_LOGIN_GUARDED_ROUTE),
        clearLoginGuardedRoute({ commit }: { commit: Commit }) {
            commit(SAVE_LOGIN_GUARDED_ROUTE, null);
        },
        setSportBetSlipRehydrated({ commit }: { commit: Commit }, isSportBetSlipRehydrated: boolean) {
            commit(SPORT_BETSLIP_REHYDRATED, isSportBetSlipRehydrated);
        },
        removeEventQueryParams({ getters, dispatch }: ActionContext<INavigationState, IState>) {
            const query = removeEventQueryParams(getters.route.query);
            dispatch('push', { query, path: getters.route.path });
        },
        addTopLevelNavigation({ commit }: { commit: Commit }, { key, value }: { key: NavigationContext, value: NavigationContextProps }) {
            commit(ADD_TOP_LEVEL_NAVIGATION, { key, value });
        },
        addDesktopNavigationItem({ commit }: { commit: Commit }, { key, value }: { key: NavigationContext, value: ContextNavigationItem }) {
            commit(ADD_DESKTOP_NAVIGATION_ITEM, { key, value });
        },
        addMobileNavigationItem({ commit }: { commit: Commit }, { key, value }: { key: NavigationContext, value: ContextNavigationItem }) {
            commit(ADD_MOBILE_NAVIGATION_ITEM, { key, value });
        },
        setCurrentNavigationContext({ commit }: { commit: Commit }, navigationContext: NavigationContext) {
            commit(SET_NAVIGATION_CONTEXT, navigationContext);

            // This is specific to Meticulous, as during the PoC we only track non-gaming context
            if (navigationContext === NavigationContext.Casino) {
                stopMeticulousRecording();
            }
        },
        addMobileRouteActionItems({ commit }: { commit: Commit }, mobileRouteActionItems: RouteActionItems) {
            commit(ADD_MOBILE_ROUTE_ACTION_ITEMS, mobileRouteActionItems);
        },
        setRouteSeoData({ commit }: { commit: Commit }, { url, data }: { url: string, data: Record<string, unknown> }) {
            commit(SET_ROUTE_SEO_DATA, { url, data });
        },
    },
    mutations: {
        [SAVE_LOGIN_GUARDED_ROUTE]: StoreUtil.createSimpleMutator('loginGuardedRoute'),
        [SPORT_BETSLIP_REHYDRATED]: StoreUtil.createSimpleMutator('isSportBetSlipRehydrated'),
        [ADD_TOP_LEVEL_NAVIGATION](state: INavigationState, { key, value }: { key: NavigationContext, value: NavigationContextProps }) {
            Vue.set(state.topLevelNavigations, key, {
                ...value,
                desktopItems: [
                    ...value.desktopItems,
                    ...(desktopItemsAddedByFederations[key] ?? []),
                ],
                mobileItems: [
                    ...value.mobileItems,
                    ...(mobileItemsAddedByFederations[key] ?? []),
                ],
            });
            desktopItemsAddedByFederations[key] = [];
            mobileItemsAddedByFederations[key] = [];
        },
        [ADD_DESKTOP_NAVIGATION_ITEM](state: INavigationState, { key, value }: { key: NavigationContext, value: ContextNavigationItem }) {
            if (state.topLevelNavigations[key] === undefined) {
                desktopItemsAddedByFederations[key] = [...(desktopItemsAddedByFederations[key] ?? []), value];
            } else {
                Vue.set(state.topLevelNavigations[key]!, 'desktopItems', [...(state.topLevelNavigations[key]?.desktopItems ?? []), value]);
            }
        },
        [ADD_MOBILE_NAVIGATION_ITEM](state: INavigationState, { key, value }: { key: NavigationContext, value: ContextNavigationItem }) {
            if (state.topLevelNavigations[key] === undefined) {
                mobileItemsAddedByFederations[key] = [...(mobileItemsAddedByFederations[key] ?? []), value];
            } else {
                Vue.set(state.topLevelNavigations[key]!, 'mobileItems', [...(state.topLevelNavigations[key]?.mobileItems ?? []), value]);
            }
        },
        [SET_NAVIGATION_CONTEXT](state: INavigationState, navigationContext: NavigationContext) {
            state.currentNavigationContext = navigationContext;
            localStorage.setItem('currentNavigationContext', navigationContext);
        },
        [ADD_MOBILE_ROUTE_ACTION_ITEMS](state: INavigationState, mobileRouteActionItems: RouteActionItems) {
            state.navigationConfig.mobileRouteActionItems = {
                ...state.navigationConfig.mobileRouteActionItems,
                ...mobileRouteActionItems,
            };
        },
        [SET_ROUTE_SEO_DATA](state: INavigationState, { url, data }: { url: string, data: Record<string, unknown> }) {
            Vue.set(state.routeSeoData, url, data);
        },
    },
    getters: {
        route(state: INavigationState, getters: NavigationGetters, rootState: IState) {
            return rootState.route;
        },
        isOnHomePage(state: INavigationState, getters: NavigationGetters, rootState: IState) {
            return rootState.route.name === RouteName.App.Home;
        },
        isOnOfferEventPage(state: INavigationState, getters: NavigationGetters, rootState: IState) {
            const { name } = rootState.route;
            return name === RouteName.Sport.OfferEvent;
        },
        isOnCompetitionsPerSportPage(state: INavigationState, getters: NavigationGetters, rootState: IState) {
            const { name } = rootState.route;
            return name === RouteName.Sport.SportCompetitions;
        },
        isOnCompetitionOutrightsPage(state: INavigationState, getters: NavigationGetters, rootState: IState) {
            const { name, query } = rootState.route;

            return name === RouteName.Sport.Competition
                && query?.[ExpandedEventInUrl.competitionTab]?.toString() === AvailableCompetitionTabs.Outrights;
        },
        isOnSuperAdvantagePerSportPage(state: INavigationState, getters: NavigationGetters, rootState: IState) {
            const { name } = rootState.route;
            return name === RouteName.Sport.SuperAdvantage;
        },
        isOnPriceBoostPerSportPage(state: INavigationState, getters: NavigationGetters, rootState: IState) {
            const { name } = rootState.route;
            return name === RouteName.Sport.PriceBoost;
        },
        isOnSportSocialPage(state: INavigationState, getters: NavigationGetters, rootState: IState) {
            return rootState.route.name === RouteName.Sport.Social;
        },
        isOnOfferEventLivePage(state: INavigationState, getters: NavigationGetters, rootState: IState) {
            const isLive = getLiveStatusFromQuery(rootState.route);

            return getters.isOnOfferEventPage && isLive;
        },
        isOnSportLivePage(state: INavigationState, getters: NavigationGetters, rootState: IState) {
            const { name, params: { timeFilter = null } = {} } = rootState.route;

            const isTimeFilterLive = timeFilter === TimeFilterSlug.Live;

            const isHomeLivePage = name === RouteName.App.Home && isTimeFilterLive;
            const isCategoryPerTimeLivePage = name === RouteName.Sport.CategoryPerTime && isTimeFilterLive;

            return isHomeLivePage || isCategoryPerTimeLivePage || getters.isOnOfferEventLivePage;
        },
        isOnSportsPage(state: INavigationState, getters: NavigationGetters, rootState: IState) {
            const { name, params: { timeFilter = null } = {} } = rootState.route;
            const sportRoutes = new Set<string>([
                RouteName.Sport.Favourites,
                RouteName.Sport.Competition,
                RouteName.Sport.SportCompetitions,
                RouteName.Sport.CategoryPerTime,
                RouteName.Sport.Category,
                RouteName.Sport.PrematchOffer,
                RouteName.Sport.Social,
            ]);

            return sportRoutes.has(name!) && timeFilter !== TimeFilterSlug.Live
                || (getters.isOnOfferEventPage && !getters.isOnOfferEventLivePage);
        },
        isOnPrematchPage(state: INavigationState, getters: NavigationGetters, rootState: IState) {
            const { name, params: { timeFilter = null } = {} } = rootState.route;
            const sportRoutes = new Set<string>([
                RouteName.Sport.CategoryPerTime,
                RouteName.Sport.Category,
                RouteName.Sport.PrematchOffer,
            ]);

            return sportRoutes.has(name!) && timeFilter !== TimeFilterSlug.Live;
        },
        isOnBetSlipPage(state: INavigationState, getters: NavigationGetters, rootState: IState) {
            const { path } = rootState.route;
            return path === Link.Sport.BetSlip;
        },
        isOnPromotionHubPage(state: INavigationState, getter: NavigationGetters, rootState: IState) {
            const { name: currentRouteName } = rootState.route;
            return currentRouteName === RouteName.BonusAndComms.PromotionHub;
        },
        timeFilter(state: INavigationState, getters: NavigationGetters, rootState: IState) {
            return getTimeFilter(rootState);
        },
        loginGuardedRoute: StoreUtil.createSimpleGetter('loginGuardedRoute'),
        topLevelNavigations: createSimpleGetter<INavigationState, 'topLevelNavigations'>('topLevelNavigations'),
        currentTopLevelNavigation(state: INavigationState): NavigationContextProps | undefined {
            /**
             * Can be undefined in this scenario
             * 1. you have sports set as your navigation context. It is written in localStorage
             * 2. you refresh the page
             * 3. your navigation context is loaded from localStorage which is Sports
             * 4. Sports fed still has not loaded topLevelNavigation and this returns undefined
             */
            return state.topLevelNavigations[state.currentNavigationContext];
        },
        currentNavigationContext: createSimpleGetter<INavigationState, 'currentNavigationContext'>('currentNavigationContext'),
        mobileRouteActionItems(state: INavigationState) {
            return state.navigationConfig.mobileRouteActionItems;
        },
        routeSeoData(state: INavigationState, getters: NavigationGetters, rootState: IState): SeoData | undefined {
            return state.routeSeoData[getSeoEngineUrl(rootState.route)];
        },
    },
};

export type NavigationDataStore = typeof navigationModule;

export interface NavigationGetters {
    'isOnOfferEventLivePage': boolean,
    'isOnCompetitionOutrightsPage': boolean,
    'isOnOfferEventPage': boolean,
    'isOnSportLivePage': boolean;
    'isOnBetSlipPage': boolean;
    'timeFilter': TimeFilter;
    'route': Route;
}

export function subscriptions(store: Store<IState>) {
    return store.watch(
        (state) => ({ route: state.route, rehydrated: state.navigation.isSportBetSlipRehydrated }),
        (
            { rehydrated, route },
            { route: oldRoute },
        ) => {
            const isOnBetslipPage = store.getters['navigation/isOnBetSlipPage'];
            if (isOnBetslipPage && rehydrated) {
                store.dispatch('ui/sportOffer/betSlip/addSelectionsFromUrl');
                router.replace({
                    name: RouteName.App.Home, // clear betSlip url after selections are added
                });
            }
            if (route.path !== oldRoute.path) {
                store.dispatch('ui/sportOffer/marketFilters/resetSelectedMarkets');
            }
        },
    );
}
